import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/ApiService";

const loginIdSchema = Yup.string()
  .trim()
  .required("Enter your email or mobile number")
  .test(
    "mobile-or-email",
    "Enter a valid email address or 10 digit mobile number",
    (value) => {
      if (!value) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[0-9]{10}$/;
      return emailRegex.test(value) || mobileRegex.test(value);
    },
  );

const requestSchema = Yup.object({ loginId: loginIdSchema });

const resetSchema = Yup.object({
  code: Yup.string()
    .trim()
    .required("Enter the verification code")
    .matches(/^[0-9]{4}$/, "Code must be 4 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "At least 8 characters")
    .matches(/[a-z]/, "Include a lowercase letter")
    .matches(/[A-Z]/, "Include an uppercase letter")
    .matches(/[0-9]/, "Include a number"),
  confirmPassword: Yup.string()
    .required("Confirm your password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("request");
  const [loginId, setLoginId] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    document.title = "Reset password · Orka Analytics";
  }, []);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: 460, flexShrink: 0,
        bgcolor: "#0F172A", p: 5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>OrkaAnalytics</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "1.875rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.25, letterSpacing: "-0.03em", mb: 2 }}>
            Locked out?{" "}
            <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              We&apos;ve got you.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75 }}>
            Enter your email or mobile and we&apos;ll send a 4-digit code to reset your password.
          </Typography>
        </Box>

        <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
            Remember your password?{" "}
            <Link component={RouterLink} to="/signin" sx={{ color: "#818CF8", textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              Sign in →
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#F0F2F7", p: { xs: 3, sm: 5 } }}>
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>OrkaAnalytics</Typography>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5", p: { xs: 3, sm: 4.5 }, width: "100%", maxWidth: 420, boxShadow: "0px 4px 24px rgba(15,23,42,0.07)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>
            {phase === "request" ? "Forgot password" : "Set new password"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
            {phase === "request"
              ? "Enter the email or mobile number you use to sign in. We will send a 4-digit code."
              : `Code sent for ${loginId}. Enter the code and choose a new password.`}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2.5 }}>{success}</Alert>}

          {phase === "request" ? (
            <Formik
              initialValues={{ loginId: "" }}
              validationSchema={requestSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                setSuccess(null);
                try {
                  const id = await ApiService.requestPasswordReset(values.loginId);
                  if (!id) throw new Error("Unexpected response: missing verification id");
                  setLoginId(values.loginId.trim());
                  setVerificationId(id);
                  setPhase("reset");
                  setSuccess("Check your email or SMS for the verification code.");
                } catch (e) {
                  setError(e?.message || "Could not send reset code");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField fullWidth name="loginId" label="Email or mobile" margin="normal"
                    value={values.loginId} onChange={handleChange} onBlur={handleBlur}
                    error={touched.loginId && Boolean(errors.loginId)}
                    helperText={touched.loginId && errors.loginId}
                    autoComplete="username" autoFocus />
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                    {isSubmitting ? "Sending…" : "Send code"}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{ code: "", password: "", confirmPassword: "" }}
              validationSchema={resetSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                setSuccess(null);
                try {
                  await ApiService.resetPassword({
                    verificationId,
                    verificationCode: values.code.trim(),
                    loginId,
                    password: values.password.trim(),
                  });
                  setSuccess("Password updated. You can sign in now.");
                  setTimeout(() => navigate("/signin", { replace: true }), 1200);
                } catch (e) {
                  setError(e?.message || "Reset failed");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField fullWidth name="code" label="4-digit code" margin="normal"
                    value={values.code} onChange={handleChange} onBlur={handleBlur}
                    error={touched.code && Boolean(errors.code)}
                    helperText={touched.code && errors.code}
                    inputProps={{ inputMode: "numeric", maxLength: 4 }} autoFocus />
                  <TextField fullWidth name="password" label="New password" type={showPassword ? "text" : "password"} margin="normal"
                    value={values.password} onChange={handleChange} onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                          {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}} />
                  <TextField fullWidth name="confirmPassword" label="Confirm password" type={showConfirm ? "text" : "password"} margin="normal"
                    value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur}
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end" tabIndex={-1}>
                          {showConfirm ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}} />
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                    {isSubmitting ? "Updating…" : "Update password"}
                  </Button>
                  <Button fullWidth variant="text" sx={{ mt: 1.5, color: "#64748B" }}
                    onClick={() => {
                      setPhase("request");
                      setVerificationId("");
                      setError(null);
                      setSuccess(null);
                    }}>
                    Back
                  </Button>
                </Form>
              )}
            </Formik>
          )}

          <Button fullWidth variant="text" component={RouterLink} to="/signin"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{ mt: 2, color: "#64748B", "&:hover": { bgcolor: "#F8FAFC" } }}>
            Back to sign in
          </Button>
        </Box>

        <Typography variant="caption" sx={{ color: "#94A3B8", mt: 4 }}>
          © {new Date().getFullYear()} OrkaAnalytics · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
